import { useEffect, useState } from 'react';

import { subscribeToStation } from '../../lib/realtime';

interface Tip {
  /** Local, so two tips of the same size a second apart are still two cards. */
  key: number;
  amountCents: number;
  from: string;
}

/**
 * Tips landing, while the host is looking at the console.
 *
 * ## Why this is not a notification
 *
 * The host already gets one of those, and it will still be there tomorrow. This
 * is the other half: somebody broadcasting has both hands busy and is not going
 * to open a bell menu mid-set. It has to arrive in front of them, say what
 * happened, and get out of the way on its own.
 *
 * ## In the column, not floating over it
 *
 * It was a fixed overlay, and every corner of the console turned out to belong
 * to something: top right covered the mode tabs, bottom right covered the chat
 * input. A console is an instrument panel and an alert that hides an instrument
 * is worse than one somebody might miss.
 *
 * So it sits at the top of the session column - where a host already looks to
 * see how the room is doing - and pushes the stats down for a few seconds
 * rather than covering anything at all.
 *
 * ## Pushed, never polled
 *
 * The amount comes down the station channel with the event, so nothing is
 * fetched when a tip lands. A refetch at that moment would be a request per tip
 * during exactly the busiest thing the console ever does.
 *
 * The gross is shown rather than the host's share - it is what the person
 * actually gave, and that is what is being acknowledged. What the host keeps is
 * on the notification and in their earnings.
 */
export function TipAlerts({ stationId }: { stationId: number | undefined }) {
  const [tips, setTips] = useState<Tip[]>([]);

  useEffect(() => {
    if (stationId === undefined) return;

    return subscribeToStation(stationId, (event, payload) => {
      if (event !== 'station.tip' || !payload) return;

      const data = payload as {
        amount_cents?: number;
        from?: { display_name?: string | null; verified?: boolean; username?: string } | null;
      };

      const tip: Tip = {
        key: Date.now() + Math.random(),
        amountCents: typeof data.amount_cents === 'number' ? data.amount_cents : 0,
        from: data.from?.display_name || data.from?.username || 'Someone',
      };

      /*
       * Three at once, newest first.
       *
       * A busy moment can land several in a few seconds, and a stack that grows
       * without limit covers the console - which during a broadcast is the one
       * thing that must stay usable.
       */
      setTips((current) => [tip, ...current].slice(0, 3));

      window.setTimeout(() => {
        setTips((current) => current.filter((t) => t.key !== tip.key));
      }, 9000);
    });
  }, [stationId]);

  if (tips.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2" data-tip-alerts>
      {tips.map((tip) => (
        <div
          key={tip.key}
          data-tip-alert
          className="flex items-center gap-3 rounded-[var(--radius-panel)] border border-fresh/40 bg-fresh/10 px-4 py-3"
        >
          <span className="text-lg font-semibold tabular-nums text-fresh">
            ${(tip.amountCents / 100).toFixed(2)}
          </span>
          <span className="text-sm text-ink">
            from <span className="font-medium">{tip.from}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
